import React, { useEffect, useState } from "react";
import Loader from "../components/common/Loader";
import ErrorState from "../components/common/ErrorState";
import Badge from "../components/common/Badge";
import Modal from "../components/common/Modal";
import panelUsersService from "../services/panelUsersService";

const EMPTY_FORM = { name: "", email: "", password: "" };
const EMPTY_PASSWORD_FORM = { password: "", confirmPassword: "" };

export default function PanelUsersAdmin() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD_FORM);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  function load() {
    setState("loading");
    panelUsersService
      .list()
      .then((res) => {
        setRows(res.data || []);
        setState("ready");
      })
      .catch(() => setState("error"));
  }

  useEffect(load, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await panelUsersService.create(form);
      setModalOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setError(err.friendlyMessage || "Could not create panel member.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus(p) {
    await panelUsersService.setStatus(p.id, p.is_active ? 0 : 1);
    load();
  }

  function openPasswordModal(p) {
    setPasswordTarget(p);
    setPasswordForm(EMPTY_PASSWORD_FORM);
    setPasswordError("");
    setPasswordSuccess("");
    setPasswordModalOpen(true);
  }

  async function handlePasswordSave(e) {
    e.preventDefault();
    setPasswordError("");

    if (passwordForm.password.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setPasswordError("Passwords don't match.");
      return;
    }

    setPasswordSaving(true);
    try {
      await panelUsersService.resetPassword(passwordTarget.id, passwordForm.password);
      setPasswordSuccess(`Password updated for ${passwordTarget.name}. Share the new password with them directly.`);
      setPasswordForm(EMPTY_PASSWORD_FORM);
    } catch (err) {
      setPasswordError(err.friendlyMessage || "Could not update password.");
    } finally {
      setPasswordSaving(false);
    }
  }

  if (state === "loading") return <div className="pm-page"><Loader label="Loading panel members" /></div>;
  if (state === "error") return <div className="pm-page"><ErrorState onRetry={load} /></div>;

  return (
    <div className="pm-page">
      <header className="pm-page__header">
        <div>
          <span className="pm-eyebrow">Interview panel</span>
          <h1>Panel members</h1>
        </div>
        <button className="pm-btn pm-btn--primary" onClick={() => setModalOpen(true)}>+ Add panel member</button>
      </header>

      <div className="pm-card">
        <table className="pm-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <td className="pm-table__primary">{p.name}</td>
                <td className="pm-table__muted">{p.email}</td>
                <td><Badge active={!!p.is_active} /></td>
                <td>
                  <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => openPasswordModal(p)}>
                    Change password
                  </button>
                  <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => toggleStatus(p)}>
                    {p.is_active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add panel member">
        <form onSubmit={handleSave} className="pm-modal-form">
          <div className="pm-field">
            <label>Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="pm-field">
            <label>Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="pm-field">
            <label>Temporary password</label>
            <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <span className="pm-field__hint">Share this with them directly — at least 8 characters.</span>
          </div>
          {error && <div className="pm-form-error">{error}</div>}
          <button className="pm-btn pm-btn--primary" disabled={saving} type="submit">
            {saving ? "Creating…" : "Create panel login"}
          </button>
        </form>
      </Modal>

      <Modal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title={`Change password — ${passwordTarget?.name || ""}`}
      >
        <form onSubmit={handlePasswordSave} className="pm-modal-form">
          <div className="pm-field">
            <label>New password</label>
            <input
              type="password"
              required
              minLength={8}
              value={passwordForm.password}
              onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
            />
            <span className="pm-field__hint">At least 8 characters. The old password is not needed.</span>
          </div>
          <div className="pm-field">
            <label>Confirm new password</label>
            <input
              type="password"
              required
              minLength={8}
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
            />
          </div>
          {passwordError && <div className="pm-form-error">{passwordError}</div>}
          {passwordSuccess && <div className="pm-sync-message">{passwordSuccess}</div>}
          <button className="pm-btn pm-btn--primary" disabled={passwordSaving} type="submit">
            {passwordSaving ? "Updating…" : "Update password"}
          </button>
        </form>
      </Modal>
    </div>
  );
}